import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';

import { cn } from '../../shared/lib/cn';
import { Page, PageBand, PageHeader } from '../../shared/ui/layout';
import { SETTINGS_TABS, type TSettingsTab } from './tabs';
import { useOrganizationSettings } from './use-organization-settings';

interface ISettingsShellProps {
  active: TSettingsTab;
  question: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}

// One width, title and header for every tab, actions beside the question: a tab that brought
// its own moved the tab strip under the pointer as you went across it.
export const SettingsShell = ({ active, question, actions, children }: ISettingsShellProps) => {
  const { organization } = useOrganizationSettings();
  const name = organization.data?.name;

  return (
    <>
      <PageHeader
        width="wide"
        title="Settings"
        description={
          name
            ? `${name} · shared with everyone in it.`
            : 'Shared with everyone in the organization.'
        }
      />

      <PageBand width="wide" className="pt-4">
        <nav aria-label="Settings sections" className="flex gap-1">
          {SETTINGS_TABS.map((tab) => (
            <Link
              key={tab.to}
              to={tab.to}
              aria-current={tab.to === active ? 'page' : undefined}
              className={cn(
                'flex h-control-xs items-center rounded-control px-3 text-data font-medium',
                tab.to === active ? 'bg-highlight text-primary' : 'text-muted hover:text-primary',
              )}
            >
              {tab.label}
            </Link>
          ))}
        </nav>
      </PageBand>

      <Page width="wide" className="gap-5">
        <div className="flex min-h-control-sm items-center gap-4">
          <p className="min-w-0 flex-1 text-data text-muted">{question}</p>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </div>
        {children}
      </Page>
    </>
  );
};
